import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AddressValidationResponse, PatientAddress } from 'src/app/models/patient.model';
import { environment } from 'src/environments/environment';
import { SnackService } from '../snack.service';

@Injectable({
  providedIn: 'root',
})
export class AddressUSPSValidatorService {
  constructor(
    private http: HttpClient,
    private snackService: SnackService
  ) {}

  validateAddress(address: PatientAddress): Observable<AddressValidationResponse> {
    const params = new HttpParams()
      .set('streetAddress', address.address2 || '')
      .set('secondaryAddress', address.address1 || '')
      .set('city', address.city || '')
      .set('state', address.state || '')
      .set('zip5', address.zip5 || '')
      .set('zip4', address.zip4 || '');

    return this.http.get<AddressValidationResponse>(`${environment.welbyEndpoint}/api/v1/addresses/validate`, {
      params,
    });
  }

  validateAndApply(addressForm): Observable<{ valid: boolean; correctionMessage: string }> {
    console.log('addressForm', addressForm);
    const address = this.getAddressValidatorObject(addressForm);
    return this.validateAddress(address).pipe(
      take(1),
      map((resp) => {
        const validatorResponse = this.parseValidationResponse(resp);
        if (validatorResponse.error) {
          if (validatorResponse.secondaryAddressRequired) {
            addressForm.get('address2').setErrors({ correction: true });
          }
          return { valid: false, correctionMessage: validatorResponse.error };
        } else {
          addressForm.get('address2').setErrors(null);
          this.snackService.genericSnackBar('Address found! ', ['success-snackbar'], 5000);
          this.setDataFromValidatorAddress(validatorResponse, addressForm);
          return { valid: true, correctionMessage: '' };
        }
      }),
      catchError((err) => {
        this.snackService.genericSnackBar(err?.error?.message || 'Address validation failed', ['error-snackbar'], 5000);
        return of({ valid: false, correctionMessage: '' });
      })
    );
  }

  parseValidationResponse(response: AddressValidationResponse): AddressValidationResponse {
    const corrections = response?.data?.corrections;
    if (corrections?.length && corrections.some((c) => c.code)) {
      const errorText = corrections
        .filter((c) => c.code)
        .map((c) => c.text)
        .join(' ');
      const secondaryAddressRequired = corrections.some((c) => c.code === '32');
      return { ...response, error: errorText, secondaryAddressRequired };
    }
    return response;
  }

  getAddressValidatorObject(addressForm): PatientAddress {
    const address = {
      address2: addressForm.get('address').value,
      address1: addressForm.get('address2').value,
      city: addressForm.get('city').value,
      state: addressForm.get('state').value,
      zip5: addressForm.get('postalCode').value,
    } as PatientAddress;
    return address;
  }

  setDataFromValidatorAddress(validatorResponse: AddressValidationResponse, addressForm) {
    if (validatorResponse?.data?.address) {
      const addr = validatorResponse.data.address;
      addressForm.get('address').setValue(addr.streetAddress);
      addressForm.get('city').setValue(addr.city);
      addressForm.get('state').setValue(addr.state);
      addressForm.get('postalCode').setValue(String(addr.ZIPCode));
      addressForm.updateValueAndValidity();
    }
  }
}
